import { IPayroll, IPayrollRes } from "pages/dashboard/payroll/typings"
import { IDashboard, IDashboardRes } from "../typings"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "stores"
import { useState } from "react"
import Security from "assets/images/security.svg"
import { useQuery } from "@tanstack/react-query"
import { authService, AUTH_ENDPOINTS } from "services/authService"
import {
  adminPeopleService,
  ADMIN_PEOPLE_ENDPOINTS,
} from "services/adminPeopleService"
import { payrollService, PAYROLL_ENDPOINTS } from "services/payrollService"

interface I2FADetails {
  twoFactorEnabled: boolean
  twoFactorSetup: boolean | null
}
import { Progress } from "antd"
import CustomCard from "components/new-stuff/ui/CustomCard"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import { isPadeDomain } from "utils/utils"
import usePermissions from "hooks/usePermissions"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"

export const Profile = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [activeButton, setActiveButton] = useState("tab-1")
  const { colorScheme } = useCustomizationSettingsStore(
    (state) => state.customizationSettings.brand
  )
  const [canViewPayrollOverview] = usePermissions({
    allowedPermissions: ["can_view_payroll_overview"],
  })

  const { isLoading: isPersonLoading, data: dashboard } =
    useQuery<IDashboardRes>({
      queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_DASHBOARD],
      queryFn: () => adminPeopleService.getDashboard(),
      cacheTime: 24 * 60 * 60 * 1000,
    })
  const { isLoading: isPayrollLoading, data: payroll } = useQuery<IPayrollRes>({
    queryKey: [PAYROLL_ENDPOINTS.VIEW_INDEX],
    queryFn: () => payrollService.viewIndex(),
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const { data: twoFaStatus, isLoading: gettingStatus } = useQuery<
    IBaseRes<I2FADetails>
  >({
    queryKey: [AUTH_ENDPOINTS.TWO_FA_DETAILS],
    queryFn: () => authService.get2FADetails(),
  })

  return (
    <div className="">
      <h2 className="text-base font-semibold text-padeBlack font-circular mb-4">
        Profile
      </h2>

      <div
        className={`w-full ${
          canViewPayrollOverview
            ? "grid"
            : "flex flex-col sm:flex-row md:flex-col lg:flex-row"
        } grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-6 md:gap-[18px]`}
      >
        <div
          id="dashboard-profile"
          className={`col-span-1 xl:col-span-2 ${
            canViewPayrollOverview ? "" : "grow w-full"
          }  `}
        >
          <CustomCard
            rootClassName="min-h-[212px]"
            activeTabKey={activeButton}
            onTabChange={(key) => setActiveButton(key as string)}
            tabList={[
              {
                key: "tab-1",
                tab: (
                  <div className="flex gap-4 ">
                    <div className="">
                      <Progress
                        rootClassName="overflow-hidden"
                        className=""
                        strokeWidth={10}
                        strokeColor={colorScheme}
                        size={50}
                        type="circle"
                        percent={
                          dashboard?.data?.person?.setup_percentage as number
                        }
                        format={() => (
                          <PersonAvatar
                            src={user?.userAvatarFileUrl}
                            alt=""
                            className=" w-[40px] aspect-square rounded-[50%] object-cover"
                          />
                        )}
                      />
                    </div>
                    <div className="flex-grow">
                      <p className="mb-1 text-sm font-semibold font-circular  text-neutral500">
                        {dashboard?.data?.person?.full_name}
                      </p>
                      <p className="mb-4 text-xs font-medium text-subText3">
                        {dashboard?.data?.person?.employee_title ||
                          dashboard?.data?.person?.identification_number}
                      </p>
                      <p className="mb-4 text-sm text-subText3">
                        Your profile is{" "}
                        {dashboard?.data?.person?.setup_percentage}% complete
                      </p>
                      {dashboard?.data?.person?.setup_percentage &&
                        dashboard?.data?.person?.setup_percentage < 100 && (
                          <button
                            className="py-2  px-4 font-semibold text-primary500 bg-transparent text-xs rounded-[13px] border border-primary500"
                            onClick={() => navigate("/dashboard/profile")}
                          >
                            Complete your Profile
                          </button>
                        )}
                    </div>
                  </div>
                ),
                label: "Personal",
              },
              {
                key: "tab-2",
                tab: (
                  <div className="flex gap-4 ">
                    <div className="">
                      <Progress
                        rootClassName="overflow-hidden"
                        className=""
                        strokeWidth={10}
                        strokeColor={colorScheme}
                        size={50}
                        type="circle"
                        percent={user?.setupPercentage ?? 0}
                        format={() => (
                          <img
                            src={
                              user?.organisationAvatarFileUrl ||
                              "https://padefiles.blob.core.windows.net/images/8a3c529b-4891-4dc7-81b9-fba39e8c5b74.svg "
                            }
                            alt="user"
                            className=" w-[40px] aspect-square rounded-[50%] object-cover"
                          />
                        )}
                      />
                    </div>
                    <div className="flex-grow">
                      <p className="mb-1 text-sm font-semibold  text-neutral500">
                        {user?.userOrganisation}
                      </p>
                      <p className="mb-4 text-xs font-medium text-subText3">
                        {user?.userOrganisationEmail}
                      </p>
                      <p className="mb-4 text-sm text-subText3">
                        Your profile is {user?.setupPercentage}% complete
                      </p>

                      {user?.setupPercentage !== undefined &&
                        user?.setupPercentage < 100 && (
                          <button
                            className="py-2 px-4 font-semibold text-primary500 bg-transparent text-xs rounded-[13px] border border-primary500"
                            onClick={() =>
                              navigate("/dashboard/company-details")
                            }
                          >
                            Complete your Profile
                          </button>
                        )}
                    </div>
                  </div>
                ),
                label: "Company",
              },
              {
                key: "tab-3",
                tab: (
                  <div
                    className={`flex gap-4 ${
                      twoFaStatus?.data?.twoFactorEnabled &&
                      twoFaStatus?.data?.twoFactorSetup &&
                      "bg-success100"
                    } ${
                      twoFaStatus?.data?.twoFactorEnabled &&
                      !twoFaStatus?.data?.twoFactorSetup &&
                      "bg-red-100"
                    }  p-2`}
                  >
                    <div className=" flex justify-center items-center  relative h-[57px] w-[57px]">
                      <img
                        src={Security}
                        alt="security"
                      />
                    </div>
                    {twoFaStatus?.data?.twoFactorEnabled &&
                    twoFaStatus?.data?.twoFactorSetup ? (
                      <div className={`flex-grow`}>
                        <p className="mb-2 text-sm font-semibold  text-neutral500">
                          Your account is secured
                        </p>
                        <p className="mb-4 text-xs  text-subText3">
                          Two-factor authentication keeps the bad guys out, even
                          if they know your password.
                        </p>

                        <button
                          className="py-2  px-4 font-semibold text-primary500 bg-transparent text-xs rounded-[13px] border border-primary500"
                          onClick={() => {
                            navigate("/dashboard/profile")
                          }}
                        >
                          Account preferences
                        </button>
                      </div>
                    ) : (
                      <div className={`flex-grow`}>
                        <p className="mb-2 text-sm font-semibold  text-neutral500">
                          Secure your account
                        </p>
                        <p className="mb-4 text-xs  text-subText3">
                          Two-factor authentication is the best way to keep your
                          account safe
                        </p>

                        <button
                          className="py-2  px-4 font-semibold text-primary500 bg-transparent text-xs rounded-[13px] border border-primary500"
                          onClick={() => {
                            navigate("/dashboard/profile")
                          }}
                        >
                          Set up your 2FA
                        </button>
                      </div>
                    )}
                  </div>
                ),
                label: "Security",
              },
            ]}
            loading={isPersonLoading}
          />
        </div>

        <div
          id="dashboard-next-payroll"
          className={
            "min-h-[150px] relative w-full grow bg-primary500 rounded-lg p-7 xl:py-4 xl:px-5 shadow-card col-span-1 xl:col-span-2"
          }
        >
          {isPadeDomain() === true && (
            <div
              className="opacity-50 bg-center bg-cover absolute inset-0 z-[1]"
              style={{
                backgroundImage: `url(https://padefiles.blob.core.windows.net/images/8273822e-4938-498a-a110-8736d5b5ad21.png)`,
              }}
            />
          )}
          <div className="relative z-[2] h-full ">
            <h2 className="font-extrabold text-xl text-a11y mb-5 xl:mb-4 font-circular ">
              Next Payroll
              {isPayrollLoading
                ? ""
                : !payroll?.data?.nextRunDateIsToday
                ? ` (${
                    payroll?.data?.nextRunDate_Short &&
                    payroll?.data?.nextRunDate_Short !== null
                      ? payroll?.data?.nextRunDate_Short
                      : ""
                  })`
                : ` is today`}
            </h2>
            <p className="font-semibold text-sm text-a11y max-w-[350px] xl:max-w-[250px] font-avenir ">
              {isPayrollLoading
                ? ""
                : payroll?.data && !payroll?.data?.autoPayrollEnabled
                ? `Please submit payroll before close of business ${
                    payroll?.data?.nextRunDateIsToday
                      ? "today"
                      : `on ${payroll?.data?.nextRunDate}`
                  } to pay your employees on time for their hardwork.`
                : `Payroll will automatically run before close of business  ${
                    payroll?.data?.nextRunDateIsToday
                      ? "today"
                      : `on ${payroll?.data?.nextRunDate}`
                  }.`}
            </p>
            <div>
              <img
                src={
                  "https://padefiles.blob.core.windows.net/images/efbb9f59-d437-49e5-9081-70678b23a01e.svg"
                }
                className="absolute w-[50px] h-[50px] right-4 xl:-right-2 bottom-2 xl:-bottom-2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
